// @givmo/ai — the assistant analytics taxonomy. The web client REUSES the iOS
// event names + parameter shapes 1:1 (`givmo-ios` `Core/Analytics/AssistantAnalytics.swift`)
// — NO parallel event names (spec §7). The actual GA4/Firebase emit is wired by
// `@givmo/analytics` in the 08 consuming surface; this package only owns the
// typed vocabulary + a thin emitter seam so the renderers/stream client can fire
// structural events without depending on the analytics package.
//
// Logging discipline (ported from iOS): message/chat TEXT is NEVER logged — only
// structural signals (entry point, route type, rating, latency bucket, block type,
// error code). The typed payloads below deliberately carry no free message text.

/** The assistant analytics event names (must match iOS verbatim). */
export const ASSISTANT_EVENTS = {
  opened: 'assistant_opened',
  consentGranted: 'assistant_consent_granted',
  consentDeclined: 'assistant_consent_declined',
  conversationStarted: 'conversation_started',
  messageSent: 'message_sent',
  turnCompleted: 'turn_completed',
  streamResumed: 'stream_resumed',
  uiBlockRendered: 'ui_block_rendered',
  chipTapped: 'chip_tapped',
  actionChipConverted: 'action_chip_converted',
  escalationProposed: 'escalation_proposed',
  escalationConfirmed: 'escalation_confirmed',
  feedbackGiven: 'feedback_given',
  reportFiled: 'report_filed',
  limitHit: 'limit_hit',
  errorShown: 'error_shown',
  historyOpened: 'assistant_history_opened',
  conversationResumed: 'assistant_conversation_resumed',
  conversationDeleted: 'assistant_conversation_deleted',
  newConversation: 'assistant_new_conversation',
} as const;

export type AssistantEventName = (typeof ASSISTANT_EVENTS)[keyof typeof ASSISTANT_EVENTS];

/** The structural-only parameters per event (no chat text — logging discipline). */
export type AssistantEventParams =
  | { name: typeof ASSISTANT_EVENTS.opened; params: { entry_point: string } }
  | { name: typeof ASSISTANT_EVENTS.consentGranted; params?: undefined }
  | { name: typeof ASSISTANT_EVENTS.consentDeclined; params?: undefined }
  | { name: typeof ASSISTANT_EVENTS.conversationStarted; params?: undefined }
  | { name: typeof ASSISTANT_EVENTS.messageSent; params?: undefined }
  | {
      name: typeof ASSISTANT_EVENTS.turnCompleted;
      params: { latency_bucket: string; tool_count: number };
    }
  | { name: typeof ASSISTANT_EVENTS.streamResumed; params?: undefined }
  | { name: typeof ASSISTANT_EVENTS.uiBlockRendered; params: { type: string } }
  | { name: typeof ASSISTANT_EVENTS.chipTapped; params: { route_type: string } }
  | { name: typeof ASSISTANT_EVENTS.actionChipConverted; params: { route: string } }
  | { name: typeof ASSISTANT_EVENTS.escalationProposed; params?: undefined }
  | { name: typeof ASSISTANT_EVENTS.escalationConfirmed; params?: undefined }
  | { name: typeof ASSISTANT_EVENTS.feedbackGiven; params: { rating: string } }
  | { name: typeof ASSISTANT_EVENTS.reportFiled; params: { category: string } }
  | { name: typeof ASSISTANT_EVENTS.limitHit; params: { bucket: string } }
  | { name: typeof ASSISTANT_EVENTS.errorShown; params: { code: string } }
  | { name: typeof ASSISTANT_EVENTS.historyOpened; params?: undefined }
  | { name: typeof ASSISTANT_EVENTS.conversationResumed; params?: undefined }
  | { name: typeof ASSISTANT_EVENTS.conversationDeleted; params?: undefined }
  | { name: typeof ASSISTANT_EVENTS.newConversation; params?: undefined };

/** The emit seam the consuming surface (08) implements against `@givmo/analytics`.
 * Renderers receive an optional `emit` and fire `ui_block_rendered` / `chip_tapped`
 * structurally. */
export type AssistantAnalyticsEmit = (event: AssistantEventParams) => void;
